<template>
  <table>
    <thead>
      <tr v-if="title">
        <th class="text-center" :colspan="colspan">{{ title }}</th>
      </tr>
      <tr>
        <th @click="sortBy('released')">
          Released
          <template v-if="sorting.by == 'released'">
            <v-icon class="float-right" v-if="sorting.dir == 1">mdi-chevron-down</v-icon>
            <v-icon class="float-right" v-if="sorting.dir == -1">mdi-chevron-up</v-icon>
          </template>
        </th>
        <th @click="sortBy('title')">
          Title
          <template v-if="sorting.by == 'title'">
            <v-icon class="float-right" v-if="sorting.dir == 1">mdi-chevron-down</v-icon>
            <v-icon class="float-right" v-if="sorting.dir == -1">mdi-chevron-up</v-icon>
          </template>
        </th>
        <th>Director</th>
        <th>Genres</th>
        <th @click="sortBy('runtime')">
          Runtime
          <template v-if="sorting.by == 'runtime'">
            <v-icon class="float-right" v-if="sorting.dir == 1">mdi-chevron-down</v-icon>
            <v-icon class="float-right" v-if="sorting.dir == -1">mdi-chevron-up</v-icon>
          </template>
        </th>
        <th class="text-center" @click="sortBy('rating')">
          Rating
          <template v-if="sorting.by == 'rating'">
            <v-icon class="float-right" v-if="sorting.dir == 1">mdi-chevron-down</v-icon>
            <v-icon class="float-right" v-if="sorting.dir == -1">mdi-chevron-up</v-icon>
          </template>
        </th>
        <th class="text-center" @click="sortBy('watched')">
          Watched
          <template v-if="sorting.by == 'watched'">
            <v-icon class="float-right" v-if="sorting.dir == 1">mdi-chevron-down</v-icon>
            <v-icon class="float-right" v-if="sorting.dir == -1">mdi-chevron-up</v-icon>
          </template>
        </th>
        <th class="text-center">Owned</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(film, index) in renderedFilms" :key="film.id">
        <td>
          <router-link :to="links.dateLink(film.release_date)">
            {{ film.release_date }}
          </router-link>
        </td>
        <td>
          <div v-if="hasMore && index == renderedFilms.length - 10" v-intersect="onIntersect"></div>
          <router-link :to="links.filmLink(film)">
            {{ film.title }}
          </router-link>
        </td>
        <td><chip-group query="person" :values="film.directors" :max-shown="2" /></td>
        <td><chip-group query="genre" :values="film.genres" /></td>
        <td>{{ utils.runtimeText(film) }}</td>
        <td class="text-center">
          <film-rating :id="film.id" :rating="film.avg_rating" />
        </td>
        <td class="text-center">
          <my-rating
            :id="film.id"
            :rating="utils.userRating(film, me.id)"
            @updateFilm="updateFilm(index, $event)"
          />
        </td>
        <td class="text-center">
          <film-ownership
            :id="film.id"
            :owned="film.owned"
            @updateFilm="updateFilm(index, $event)"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import links from "@/utils/Links"
import utils from "@/utils/utils"
import FilmRating from "@/components/details/FilmRating"
import MyRating from "@/components/details/MyRating"
import ChipGroup from "@/components/details/ChipGroup"
import FilmOwnership from "@/components/details/FilmOwnership"

export default {
  name: "FilmTable",
  components: {
    FilmRating,
    MyRating,
    ChipGroup,
    FilmOwnership,
  },
  props: {
    query: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      films: [],
      numShown: 50,
      sorting: {
        by: "",
        dir: 1,
      }
    }
  },
  inject: ["httpClient"],
  mounted() {
    this.load()
  },
  computed: {
    me() {
      return this.$store.state.me
    },
    links() {
      return links
    },
    utils() {
      return utils
    },
    colspan() {
      return 8
    },
    renderedFilms() {
      return this.films.slice(0, this.numShown)
    },
    hasMore() {
      return this.films.length > this.renderedFilms.length
    },
  },
  methods: {
    async load() {
      this.films = []
      const films = []
      await this.httpClient.loadAllFilms({
        query: {
          ...this.query,
          annotate_rating: this.me.id
        },
        callback: async newFilms => {
          if (this.films.length == 0) this.films = newFilms
          films.splice(films.length, 0, ...newFilms)
        }
      })
      this.films = films
      this.resetSorting()
    },
    updateFilm(index, data) {
      const film = Object.assign(this.films[index], data)
      this.films.splice(index, 1, film)
    },
    onIntersect(entry, obverser, isIntersecting) {
      if (isIntersecting) {
        this.numShown += 50
      }
    },
    resetSorting() {
      this.sorting = {by: "", dir: 1}
    },
    sortBy(column) {
      if (this.sorting.by == column) this.sorting = {by: this.sorting.by, dir: -1 * this.sorting.dir}
      else this.sorting = {by: column, dir: 1}
    },
  },
  watch: {
    query() {
      this.load()
      // TODO: override search bar if there's a query
    },
    sorting() {
      switch (this.sorting.by) {
        case "released":
          this.films.sort(utils.numericCompare(this.sorting.dir, film => film.release_date))
          break;
        case "title":
          this.films.sort(utils.stringCompare(this.sorting.dir, film => film.title))
          break;
        case "runtime":
          this.films.sort(utils.numericCompare(this.sorting.dir, film => film.runtime))
          break;
        case "rating":
          this.films.sort(utils.numericCompare(this.sorting.dir, film => film.avg_rating))
          break;
        case "watched":
          this.films.sort(utils.numericCompare(this.sorting.dir, film => utils.userRating(film, this.me.id)))
          break;
      }
    }
  },
}
</script>
